import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';

import { FileService } from '../../../services/file.service';
import { activeDailyReportDate } from '../../../services/chat-daily-report.service';
import { DailyReportResult } from '../../../types/Chatbot';

const MILLISECONDS_PER_DAY = 86_400_000;

@Component({
  selector: 'app-chat-daily-report-card',
  imports: [MatButtonModule, MatIconModule, TranslateModule, MarkdownModule],
  templateUrl: './chat-daily-report-card.component.html',
  styleUrl: './chat-daily-report-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatDailyReportCardComponent {
  private readonly fileService = inject(FileService);

  public readonly result = input.required<DailyReportResult>();
  public readonly dismissed = output<void>();

  public readonly report = computed(() => {
    const result = this.result();
    return result.kind === 'merged' ? result.report : null;
  });

  // The ZIP filename is daily-reports-<date>.zip; derive its report date from the filename.
  public readonly reportDate = computed(() => {
    const result = this.result();
    if (result.kind === 'merged') {
      return result.report.date;
    }
    return /(\d{4}-\d{2}-\d{2})/.exec(result.filename)?.[1] ?? null;
  });

  public readonly reportAgeInDays = computed(() => {
    const reportDate = this.reportDate();
    return reportDate ? (Date.parse(activeDailyReportDate()) - Date.parse(reportDate)) / MILLISECONDS_PER_DAY : null;
  });

  public openReport(): void {
    const result = this.result();
    if (result.kind !== 'merged') {
      return;
    }
    const url = result.report.url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  public downloadZip(): void {
    const result = this.result();
    if (result.kind !== 'zip') {
      return;
    }
    this.fileService.downloadBlob(result.blob, result.filename);
  }
}
